// Hideo - the background layer ("Backgrounds" in Settings › Appearance): a pattern behind the pages, glass panels and a
// fine grain, on top of any theme (Default included). Without UI: the stored choice, what each theme allows, the classes on
// <html>. Applied by the theme store (src/stores/theme.ts), chosen with BackdropPicker.vue, drawn by CSS only
// (src/styles/themes.css, "background layer"): the pattern is body::before and the grain body::after, both fixed behind the
// page (z-index -1, above the theme's canvas at -2), coloured with the theme's tokens, so they follow every theme and the
// main colour. The anti-flash script in index.html's <head> puts the same classes before anything is drawn (it works the
// locks out from the theme's family classes; tests/backdrop.test.ts checks it agrees with backdropClasses).
//
// Stored on this device: creatorOS_backdrop = JSON { bg, glass, grain } (missing or broken = nothing on). What a theme
// allows (backdropLocks): a theme with its own background (Blurred bubbles, Nostalgia Channel, the four with their own
// colours) keeps it, so no pattern; the four with their own colours lock the whole section; glass is decided by the theme
// for Blurred bubbles and Aero (always on: their own rule) and for Nostalgia, Four Keys and High contrast (always off).
import { styleDef } from './appThemes'

export const BACKDROP_KEY = 'creatorOS_backdrop'

/** The patterns: none, Aurora (mesh of soft colours), dots, grid, topographic lines, blueprint (Tech HUD) */
export type BackdropBg = 'none' | 'aurora' | 'dots' | 'grid' | 'topo' | 'hud'
export interface Backdrop { bg: BackdropBg; glass: boolean; grain: boolean }
export const DEFAULT_BACKDROP: Readonly<Backdrop> = { bg: 'none', glass: false, grain: false }

/** The picker's patterns, in order: key, English name and description (t(); Italian in src/i18n/it/core.ts) */
export const BACKDROP_BGS: readonly [BackdropBg, string, string][] = [
  ['none', 'None', 'just the theme'],
  ['aurora', 'Aurora', 'soft shades of your colour that drift slowly'],
  ['dots', 'Dots', 'a fine grid of dots'],
  ['grid', 'Grid', 'thin squared lines'],
  ['topo', 'Contour lines', 'like a map of mountains'],
  ['hud', 'Blueprint', 'a technical grid with targets'],
]
const BGS = new Set<string>(BACKDROP_BGS.map(([k]) => k))

/** A stored value (JSON text or an object) made safe: unknown values give the defaults */
export function parseBackdrop(raw: unknown): Backdrop {
  let o: unknown = raw
  if (typeof raw === 'string') { try { o = JSON.parse(raw) } catch { o = null } }
  if (!o || typeof o !== 'object') return { ...DEFAULT_BACKDROP }
  const r = o as Record<string, unknown>
  return { bg: typeof r.bg === 'string' && BGS.has(r.bg) ? r.bg as BackdropBg : 'none', glass: r.glass === true, grain: r.grain === true }
}
/** The choice stored on this device (blocked storage = nothing on) */
export function readBackdrop(): Backdrop {
  try { return parseBackdrop(localStorage.getItem(BACKDROP_KEY)) } catch { return { ...DEFAULT_BACKDROP } }
}
/** Stores the choice (nothing on removes the key) */
export function storeBackdrop(b: Backdrop): void {
  try {
    if (b.bg === 'none' && !b.glass && !b.grain) localStorage.removeItem(BACKDROP_KEY)
    else localStorage.setItem(BACKDROP_KEY, JSON.stringify({ bg: b.bg, glass: b.glass, grain: b.grain }))
  } catch { /* storage full or blocked */ }
}

/** What the theme shown allows. pattern / grain: false = locked (not applied, shown disabled); glass: 'user' = the
 *  switch decides, 'on' / 'off' = the theme decides */
export interface BackdropLocks { all: boolean; pattern: boolean; glass: 'on' | 'off' | 'user'; grain: boolean }
export function backdropLocks(key: string): BackdropLocks {
  const s = styleDef(key)
  if (!s) return { all: false, pattern: true, glass: 'user', grain: true }
  return { all: !s.adaptive, pattern: s.adaptive && !s.ownSky, glass: s.glass, grain: s.adaptive }
}

/** Every class the layer may put on <html> */
export const BACKDROP_CLASSES = ['bd-aurora', 'bd-dots', 'bd-grid', 'bd-topo', 'bd-hud', 'bd-glass', 'bd-grain'] as const

/** The classes on <html> for a theme and a choice (glass forced on by a theme is its own rule: no class) */
export function backdropClasses(key: string, b: Backdrop): string[] {
  const l = backdropLocks(key), c: string[] = []
  if (l.pattern && b.bg !== 'none') c.push('bd-' + b.bg)
  if (l.glass === 'user' && b.glass) c.push('bd-glass')
  if (l.grain && b.grain) c.push('bd-grain')
  return c
}
/** Puts the layer's classes on <html> (removing the old ones) */
export function applyBackdropToHtml(html: HTMLElement, key: string, b: Backdrop): void {
  for (const c of BACKDROP_CLASSES) html.classList.remove(c)
  for (const c of backdropClasses(key, b)) html.classList.add(c)
}
/** The pattern actually shown (for the Appearance summary), or null */
export function shownBackdrop(key: string, b: Backdrop): BackdropBg | null {
  return backdropLocks(key).pattern && b.bg !== 'none' ? b.bg : null
}
